/* ============================================================
   Market evidence — the corridor, measured.

   Everything else in this product computes something from what the
   user typed. This file is the exception: it is the published record
   of what the corridor costs, and it exists so the profile page can
   answer "why should I run any of this?" before asking anyone to.

   Which makes it the most dangerous data in the repository. A
   diagnostic that is stale gives a wrong answer about the user's own
   numbers, and they will usually notice. A market figure that is stale
   gives a confident wrong answer about the world, cited to a real
   source, and nobody notices for a year.

   So every figure carries three things, and check-data enforces all
   three: the date it was published (asOf), how often the publisher
   issues a new one (cadence), and where it came from. The staleness
   rule is cadence-aware rather than a flat threshold — an annual
   survey at thirteen months has missed one edition; a quarterly
   observatory at thirteen months has missed four, and is a different
   kind of wrong.

   These figures were transcribed from a company profile deck. That is
   exactly the failure mode this file prevents: a deck is a frozen
   artefact whose numbers decay silently while still carrying their
   sources. Here they decay loudly.
   ============================================================ */

export const CADENCE_MONTHS = { annual: 12, semiannual: 6, quarterly: 3, biennial: 24 };

/* How long past its own publication cycle a figure may drift before the
   build complains. One cycle late is a publisher running behind; two is
   the product quoting history. */
export const GRACE_MONTHS = 3;

export const MARKET = [
  {
    id: 'logistics-cost-share',
    stat: '35–42%',
    label: 'of production cost is transport and logistics',
    detail:
      'Against roughly 8% in Asian economies — a competitiveness tax on everything the ' +
      'region makes.',
    source: 'Shippers Council of Eastern Africa — Logistics Performance Survey',
    asOf: '2021-12-31',
    cadence: 'annual',
    /* Deliberately not suppressed for age. A structural cost share does
       not move quarter to quarter, and the honest presentation is the
       old date next to the number rather than a fresher figure nobody
       published. The staleness note on screen says so. */
    structural: true
  },
  {
    id: 'transit-days',
    stat: '5.7 → 4.1 days',
    label: 'Mombasa–Kampala transit, GPS surveys',
    detail:
      'Border crossing fell from about 2.5 days in 2013 to roughly 6 hours under the ' +
      'one-stop border post and Single Customs Territory — yet the corridor still carries ' +
      'about 66% of East African cargo.',
    source: 'Northern Corridor Transit and Transport Coordination Authority — Transport Observatory',
    asOf: '2025-09-30',
    cadence: 'quarterly'
  },
  {
    id: 'roadblocks',
    stat: '6–9',
    label: 'police stops per truck in Kenya and Uganda',
    detail:
      'Plus weighbridge stops, border formalities and clearance delays — friction priced ' +
      'into every tonne-kilometre.',
    source: 'Shippers Council of Eastern Africa — Logistics Performance Survey',
    asOf: '2021-12-31',
    cadence: 'annual',
    structural: true
  },
  {
    id: 'container-traffic',
    stat: '2.11M TEU',
    label: 'container traffic through Mombasa, up 5.5%',
    detail:
      'Berths 19B, 23 and 24 will add 1.4M TEU of capacity; the terminal operating system ' +
      'is about 40% upgraded and gate automation about 60% complete at gates 23–24.',
    source: 'Kenya Ports Authority — annual results briefing',
    asOf: '2026-01-31',
    cadence: 'annual'
  },
  {
    id: 'transit-cargo',
    stat: '15.88M t',
    label: 'transit cargo, up 19.5% — the fastest-growing segment',
    detail:
      'Transit is the contestable cargo: it chooses its corridor. Imports were 78% of ' +
      'Q3 2025 throughput.',
    source: 'Kenya Ports Authority; Northern Corridor Transport Observatory Q3 2025',
    asOf: '2026-01-31',
    cadence: 'annual'
  },
  {
    id: 'dwell-target',
    stat: '96h → 48h',
    label: "the charter's dwell-time target",
    detail:
      'Mombasa Port Community Charter targets: cargo dwell from a 96-hour baseline toward ' +
      '48; truck port-to-Malaba from 84 hours toward 36; the SGR Mombasa–Nairobi train ' +
      'trip from 84 hours toward 36.',
    source: 'Mombasa Port Community Charter — KPI framework',
    asOf: '2026-01-31',
    cadence: 'annual'
  }
];

/* The four layers between a quoted freight rate and a landed cost.
   Each one names the tool that computes it, so the page is an index
   into the product rather than an essay about it. A layer whose `tool`
   does not resolve to a live tool fails the build — a narrative that
   promises a calculator the product does not have is worse than no
   narrative. */
export const COST_LAYERS = [
  {
    id: 'duty',
    title: 'Duty and levies, in a strict order',
    body:
      "The published order of assessment runs customs value (CIF) → import duty at the EAC " +
      'Common External Tariff band → excise on CIF plus duty → VAT at 16% → Import ' +
      'Declaration Fee at 2.5% of CIF → Railway Development Levy at 2% of CIF. Small ' +
      'judgement calls — like whether IDF and RDL sit inside the VAT base — move real money.',
    tool: 'landed-cost'
  },
  {
    id: 'demurrage',
    title: 'Demurrage and port exposure',
    body:
      'The 2025 port tariff repriced storage and handling; clearance delays convert directly ' +
      'into demurrage, and the meter runs daily whether the delay was yours or not.',
    tool: 'landed-cost'
  },
  {
    id: 'deadlines',
    title: 'Regulatory deadlines that arrive unannounced',
    body:
      'An operator planning against a hardcoded status can be preparing for an obligation ' +
      'that no longer exists — or missing one that does. The register below computes each ' +
      "instrument's status from today's date rather than storing it.",
    tool: 'compliance'
  },
  {
    id: 'modal',
    title: 'Modal comparisons that lie',
    body:
      'Published corridor tariffs are line haul only. First and last mile, lift charges and ' +
      'ICD storage must be added before a road-versus-rail comparison is honest — and the ' +
      'obvious modal choice can invert once they are.',
    tool: 'benchmark'
  }
];

/**
 * Age a dated figure against its own publication cycle.
 * @returns {{months:number, cycle:number, pastCycle:boolean, overdue:boolean}}
 */
export function figureAge(entry, now = new Date()) {
  const months = (now - new Date(entry.asOf)) / (1000 * 60 * 60 * 24 * 30.44);
  const cycle = CADENCE_MONTHS[entry.cadence] ?? 12;
  return {
    months,
    cycle,
    pastCycle: months > cycle,
    overdue: months > cycle + GRACE_MONTHS
  };
}
